import { useId, useState } from 'react';
import { AlertCircle, FilePlus2, Loader } from 'lucide-react';
import { partnersApi } from '../../services/partnersApi';
import {
  PARTNER_WORLD_COMPATS,
  type PartnerReleaseState,
  type PartnerWorldCompat,
  type ReportView,
} from '../../types/partners';
import { cn } from '../../utils/cn';
import { releaseStateLabel, worldCompatLabel } from '../../utils/partnerStatus';
import { describeReportOutcome, type PartnerNotice } from '../../utils/partnerSync';

interface PartnerReportFormProps {
  creatorId: string;
  packs: { modpack_id: string; name: string }[];
  /** Only a partner Discord manages has a channel to post the report in. */
  managed: boolean;
  onCreated: (report: ReportView) => void;
  onCancel: () => void;
  onNotice: (notice: PartnerNotice) => void;
}

const OTHER = '__other__';

/**
 * Log an update on the partner's behalf: when they told staff in a DM, on a
 * call, or before they had a channel. It goes into the same inbox as the
 * reports partners send themselves.
 */
export const PartnerReportForm = ({ creatorId, packs, managed, onCreated, onCancel, onNotice }: PartnerReportFormProps) => {
  const uid = useId();
  const [pack, setPack] = useState(packs[0]?.modpack_id ?? OTHER);
  const [packName, setPackName] = useState('');
  const [version, setVersion] = useState('');
  const [release, setRelease] = useState<PartnerReleaseState>('released');
  const [worlds, setWorlds] = useState<PartnerWorldCompat>('unsure');
  const [changelog, setChangelog] = useState('');
  const [notes, setNotes] = useState('');
  const [post, setPost] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ text: string; field: string | null } | null>(null);

  const changelogInvalid = changelog.trim() !== '' && !/^https:\/\/[^\s]+$/i.test(changelog.trim());
  const missingPack = pack === OTHER && !packName.trim();
  const canSubmit = !!version.trim() && !missingPack && !changelogInvalid && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const res = await partnersApi.createReport({
        creator_id: creatorId,
        modpack_id: pack === OTHER ? null : pack,
        ...(pack === OTHER ? { pack_name: packName.trim() } : {}),
        version: version.trim(),
        release_state: release,
        world_compat: worlds,
        changelog_url: changelog.trim() || null,
        notes: notes.trim() || null,
        post_to_partner_channel: managed && post,
      });
      onNotice(
        describeReportOutcome(
          res.discord,
          `Logged ${res.report.pack_name} ${res.report.version}. ${post && managed ? 'It is posted in the partner’s channel' : 'Only staff see it'}, and it is in the update inbox.`,
        ),
      );
      onCreated(res.report);
    } catch (e) {
      setError({ text: e instanceof Error ? e.message : 'The report could not be logged.', field: (e as { field?: string | null })?.field ?? null });
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (field: string) => error?.field === field;

  return (
    <div className="subcard p-3 md:p-4 space-y-3">
      <p className="text-sm font-semibold text-white flex items-center gap-2">
        <FilePlus2 className="w-4 h-4 text-accent-300" />
        Log an update for this partner
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label htmlFor={`${uid}-pack`} className="block text-xs font-medium text-glass-400 mb-1.5">
            Pack
          </label>
          <select id={`${uid}-pack`} className={cn('glass-select w-full', fieldError('modpack_id') && 'border-red-500/40')} value={pack} onChange={(e) => setPack(e.target.value)} disabled={saving}>
            {packs.map((p) => (
              <option key={p.modpack_id} value={p.modpack_id}>
                {p.name}
              </option>
            ))}
            <option value={OTHER}>Another pack (type its name)</option>
          </select>
          {pack === OTHER && (
            <input
              className={cn('glass-input w-full mt-2', fieldError('pack_name') && 'border-red-500/40')}
              value={packName}
              maxLength={100}
              placeholder="Pack name"
              onChange={(e) => setPackName(e.target.value)}
              disabled={saving}
              aria-label="Pack name"
            />
          )}
        </div>
        <div>
          <label htmlFor={`${uid}-version`} className="block text-xs font-medium text-glass-400 mb-1.5">
            Version
          </label>
          <input
            id={`${uid}-version`}
            className={cn('glass-input w-full font-mono', fieldError('version') && 'border-red-500/40')}
            value={version}
            maxLength={64}
            placeholder="e.g. 2.3.2"
            onChange={(e) => setVersion(e.target.value)}
            disabled={saving}
          />
          <p className="text-xs text-glass-500 mt-1">As the partner writes it. Argus matches it to CurseForge and our clusters.</p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <span className="block text-xs font-medium text-glass-400 mb-1.5">Is it out?</span>
          <div className="segmented max-w-full" role="radiogroup" aria-label="Release">
            {(['released', 'upcoming'] as PartnerReleaseState[]).map((r) => (
              <button key={r} type="button" role="radio" aria-checked={release === r} className="segmented-item" data-active={release === r} onClick={() => setRelease(r)} disabled={saving}>
                {releaseStateLabel(r)}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label htmlFor={`${uid}-worlds`} className="block text-xs font-medium text-glass-400 mb-1.5">
            Worlds
          </label>
          <select id={`${uid}-worlds`} className="glass-select w-full" value={worlds} onChange={(e) => setWorlds(e.target.value as PartnerWorldCompat)} disabled={saving}>
            {PARTNER_WORLD_COMPATS.map((w) => (
              <option key={w} value={w}>
                {worldCompatLabel(w)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor={`${uid}-changelog`} className="block text-xs font-medium text-glass-400 mb-1.5">
          Changelog link (optional)
        </label>
        <input
          id={`${uid}-changelog`}
          className={cn('glass-input w-full', (changelogInvalid || fieldError('changelog_url')) && 'border-red-500/40')}
          value={changelog}
          maxLength={500}
          placeholder="https://…"
          inputMode="url"
          onChange={(e) => setChangelog(e.target.value)}
          disabled={saving}
        />
        {changelogInvalid && <p className="text-xs text-red-400 mt-1">Changelog links must start with https://.</p>}
      </div>

      <div>
        <label htmlFor={`${uid}-notes`} className="block text-xs font-medium text-glass-400 mb-1.5">
          Notes (optional)
        </label>
        <textarea
          id={`${uid}-notes`}
          className="glass-input w-full min-h-[72px] resize-y"
          value={notes}
          maxLength={1000}
          placeholder="Anything the partner said about this update"
          onChange={(e) => setNotes(e.target.value)}
          disabled={saving}
        />
      </div>

      <label className={cn('flex items-start justify-between gap-4', !managed && 'opacity-70')}>
        <span className="min-w-0">
          <span className="block text-sm text-white">Post it in the partner’s channel</span>
          <span className="block text-xs text-glass-400">
            {managed
              ? 'The partner sees a card for it and can follow its progress. Leave this off to keep it with staff.'
              : 'Discord is not managed for this partner, so there is no channel to post in. The report stays with staff.'}
          </span>
        </span>
        <span className="switch shrink-0">
          <input type="checkbox" checked={managed && post} disabled={!managed || saving} onChange={(e) => setPost(e.target.checked)} aria-label="Post it in the partner’s channel" />
          <span />
        </span>
      </label>

      {error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle className="w-4 h-4" />
          <span>{error.text}</span>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" className="glass-button-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={() => void submit()} disabled={!canSubmit}>
          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <FilePlus2 className="w-4 h-4" />}
          Log update
        </button>
      </div>
    </div>
  );
};
